"use client";

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Loader,
  confirm,
} from "@devhop/ui";
import { LayoutGrid, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import type { WarehouseLayoutFloorRow } from "../api/client";
import {
  useCreateWarehouseLayoutFloorMutation,
  useDeleteWarehouseLayoutMutation,
  useRenameWarehouseLayoutFloorMutation,
} from "../api/queries";
import { RequireMerchantPermission } from "./RequireMerchantPermission";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";

type Props = {
  shopId: string;
  branchId: string;
  floors: WarehouseLayoutFloorRow[];
  isLoading?: boolean;
  canEdit: boolean;
  /** Open layout tab focused on this floor */
  onOpenMap?: (layoutId: string) => void;
};

export function WarehouseLayoutFloorsPanel({
  shopId,
  branchId,
  floors,
  isLoading,
  canEdit,
  onOpenMap,
}: Props) {
  const [floorName, setFloorName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");

  const createFloor = useCreateWarehouseLayoutFloorMutation(shopId);
  const renameFloor = useRenameWarehouseLayoutFloorMutation(shopId);
  const deleteFloor = useDeleteWarehouseLayoutMutation(shopId);

  async function handleCreate() {
    const name = floorName.trim();
    await createFloor.mutateAsync({
      branchId,
      ...(name ? { name } : {}),
    });
    setFloorName("");
  }

  async function handleRename(layoutId: string) {
    const name = editName.trim();
    if (!name) return;
    await renameFloor.mutateAsync({ branchId, layoutId, name });
    setEditingId(null);
    setEditName("");
  }

  async function handleDelete(layoutId: string) {
    const ok = await confirm({
      title: "\u0ea5\u0eb6\u0e9a\u0e8a\u0eb1\u0ec9\u0e99\u0ead\u0ead\u0e81\u0e88\u0eb2\u0e81\u0ec1\u0e9c\u0e99\u0e9c\u0eb1\u0e87?",
      description:
        "\u0e95\u0eb3\u0ec1\u0edc\u0ec8\u0e87\u0ec1\u0ea5\u0eb0\u0e8a\u0ec8\u0ead\u0e87\u0e8d\u0eb1\u0e87\u0e84\u0ebb\u0e87\u0ea2\u0eb9\u0ec8\u0ec3\u0e99\u0ea5\u0eb2\u0e8d\u0e81\u0eb2\u0e99 \u2014 \u0e9e\u0ebd\u0e87\u0e8a\u0eb1\u0ec9\u0e99\u0e99\u0eb5\u0ec9\u0e88\u0eb0\u0e96\u0eb7\u0e81\u0ea5\u0eb6\u0e9a\u0ead\u0ead\u0e81\u0e88\u0eb2\u0e81\u0ec1\u0e9c\u0e99\u0e9c\u0eb1\u0e87\u0ec0\u0e97\u0ebb\u0ec8\u0eb2\u0e99\u0eb1\u0ec9\u0e99.",
      actionText: "\u0ea5\u0eb6\u0e9a\u0e8a\u0eb1\u0ec9\u0e99",
      ActionProps: { variant: "destructive" },
    });
    if (!ok) return;
    await deleteFloor.mutateAsync({ branchId, layoutId });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{"\u0e8a\u0eb1\u0ec9\u0e99"}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-muted-foreground text-xs">
          {
            "\u0ec1\u0e95\u0ec8\u0ea5\u0eb0\u0e8a\u0eb1\u0ec9\u0e99\u0ea1\u0eb5\u0ec1\u0e9c\u0e99\u0e9c\u0eb1\u0e87\u0e82\u0ead\u0e87\u0e95\u0ebb\u0e99\u0ec0\u0ead\u0e87 \u2014 \u0e88\u0eb1\u0e94\u0e81\u0eb2\u0e99\u0e8a\u0eb1\u0ec9\u0e99\u0e97\u0eb5\u0ec8\u0e99\u0eb5\u0ec8 \u0e81\u0eb2\u0e99\u0ea7\u0eb2\u0e87\u0e95\u0eb3\u0ec1\u0edc\u0ec8\u0e87\u0ec3\u0e99\u0ec1\u0e97\u0eb1\u0e9a\u0ec1\u0e9c\u0e99\u0e9c\u0eb1\u0e87"
          }
        </p>

        {canEdit ? (
          <RequireMerchantPermission
            permission={MerchantPermissions.inventory.adjust}
          >
            <div className="flex flex-wrap items-end gap-2">
              <div className="min-w-50 flex-1">
                <Label>{"\u0e8a\u0eb7\u0ec8\u0e8a\u0eb1\u0ec9\u0e99"}</Label>
                <Input
                  value={floorName}
                  onChange={(e) => setFloorName(e.target.value)}
                  placeholder={"\u0e8a\u0eb1\u0ec9\u0e99 1 / \u0e8a\u0eb1\u0ec9\u0e99\u0ea5\u0ec8\u0eb2\u0e87"}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      void handleCreate();
                    }
                  }}
                />
              </div>
              <Button
                type="button"
                size="sm"
                className="gap-1"
                disabled={createFloor.isPending}
                onClick={() => void handleCreate()}
              >
                <Plus className="size-4" />
                {"\u0ec0\u0e9e\u0eb5\u0ec8\u0ea1\u0e8a\u0eb1\u0ec9\u0e99"}
              </Button>
            </div>
          </RequireMerchantPermission>
        ) : null}

        {isLoading ? (
          <div className="flex justify-center py-6">
            <Loader />
          </div>
        ) : floors.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            {
              "\u0e8d\u0eb1\u0e87\u0e9a\u0ecd\u0ec8\u0ea1\u0eb5\u0e8a\u0eb1\u0ec9\u0e99 \u2014 \u0ec0\u0e9e\u0eb5\u0ec8\u0ea1\u0e8a\u0eb1\u0ec9\u0e99 \u0eab\u0ebc\u0eb7 \u0ec0\u0e9b\u0eb5\u0e94\u0ec1\u0e97\u0eb1\u0e9a\u0ec1\u0e9c\u0e99\u0e9c\u0eb1\u0e87\u0ec0\u0e9e\u0eb7\u0ec8\u0ead\u0ec3\u0eab\u0ec9\u0ec1\u0e9c\u0e99\u0ead\u0eb1\u0e94\u0e95\u0eb0\u0ec2\u0e99\u0ea1\u0eb1\u0e94 (\u0e8a\u0eb1\u0ec9\u0e99 1)"
            }
          </p>
        ) : (
          <ul className="space-y-2">
            {floors.map((f) => (
              <li
                key={f.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border p-3"
              >
                <div className="min-w-0 flex-1">
                  {editingId === f.id ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <Input
                        value={editName}
                        className="max-w-xs"
                        autoFocus
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            void handleRename(f.id);
                          }
                          if (e.key === "Escape") {
                            setEditingId(null);
                          }
                        }}
                      />
                      <Button
                        type="button"
                        size="sm"
                        disabled={renameFloor.isPending || !editName.trim()}
                        onClick={() => void handleRename(f.id)}
                      >
                        {"\u0e9a\u0eb1\u0e99\u0e97\u0eb6\u0e81"}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditingId(null)}
                      >
                        {"\u0e8d\u0ebb\u0e81\u0ec0\u0ea5\u0eb5\u0e81"}
                      </Button>
                    </div>
                  ) : (
                    <>
                      <p className="font-medium">{f.name}</p>
                      <p className="font-mono text-muted-foreground text-xs">
                        F{f.floorIndex + 1}
                        {f.nodeCount > 0
                          ? ` · ${f.nodeCount} \u0e9a\u0ebb\u0e99\u0e9c\u0eb1\u0e87`
                          : ` · \u0e9c\u0eb1\u0e87\u0ea7\u0ec8\u0eb2\u0e87`}
                      </p>
                    </>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-1">
                  {onOpenMap ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="gap-1"
                      onClick={() => onOpenMap(f.id)}
                    >
                      <LayoutGrid className="size-3.5" />
                      {"\u0ec1\u0e9c\u0e99\u0e9c\u0eb1\u0e87"}
                    </Button>
                  ) : null}
                  {canEdit && editingId !== f.id ? (
                    <RequireMerchantPermission
                      permission={MerchantPermissions.inventory.adjust}
                    >
                      <>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEditingId(f.id);
                            setEditName(f.name);
                          }}
                        >
                          {"\u0e9b\u0ec8\u0ebd\u0e99\u0e8a\u0eb7\u0ec8"}
                        </Button>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          disabled={deleteFloor.isPending}
                          title={"\u0ea5\u0eb6\u0e9a\u0e8a\u0eb1\u0ec9\u0e99"}
                          onClick={() => void handleDelete(f.id)}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </>
                    </RequireMerchantPermission>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
